import { forwardRef, type HTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react';
import { InfoIcon } from './icons';

/** White bordered card (profile sections, insurance block). */
export function Card({ className = '', ...rest }: HTMLAttributes<HTMLElement>) {
  return (
    <section
      className={`flex flex-col gap-2.5 rounded-card border border-line bg-paper p-4 ${className}`}
      {...rest}
    />
  );
}

/** Grey recap box (selected formats on 02, order summary on 03, team details on 07). */
export function Recap({ className = '', ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={`flex flex-col gap-2 rounded-control bg-surface p-3.5 text-[15px] ${className}`}
      {...rest}
    />
  );
}

/** One "label … value" line inside a Recap. */
export function RecapRow({
  label,
  value,
  strong,
}: {
  label: ReactNode;
  value: ReactNode;
  strong?: boolean;
}) {
  return (
    <div
      className={`flex justify-between gap-3 ${strong ? 'border-t border-line pt-2 font-bold' : ''}`}
    >
      <span>{label}</span>
      <span className={strong ? '' : 'font-semibold'}>{value}</span>
    </div>
  );
}

/** Info note with an icon (screens 04, 05, 07). `outline` = bordered instead of grey. */
export function Notice({
  title,
  children,
  icon,
  variant = 'surface',
  className = '',
}: {
  title?: ReactNode;
  children: ReactNode;
  icon?: ReactNode;
  variant?: 'surface' | 'outline';
  className?: string;
}) {
  return (
    <div
      className={`flex items-start gap-2.5 rounded-control px-3.5 py-3 text-helper text-ink-muted ${
        variant === 'surface' ? 'bg-surface' : 'border border-line'
      } ${className}`}
    >
      <span className="mt-px shrink-0">{icon ?? <InfoIcon />}</span>
      <div className="flex flex-col gap-1">
        {title && <span className="text-sm font-bold text-ink">{title}</span>}
        <span>{children}</span>
      </div>
    </div>
  );
}

/**
 * Small pill: `accent` (Early bird), `neutral` (Complet, Capitaine, Non remboursable),
 * `success` (Assurée: a done state), `danger` (back-office statuses: refused, failed).
 */
export function Badge({
  tone = 'neutral',
  keepCase,
  className = '',
  children,
}: {
  tone?: 'accent' | 'neutral' | 'success' | 'danger';
  /** Text shown as written, not upper-cased (e.g. a partner's wording that must not change). */
  keepCase?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const tones = {
    accent: 'border-accent-line bg-paper text-accent',
    neutral: 'border-line-strong text-ink-muted',
    success: 'border-success text-success',
    danger: 'border-danger text-danger',
  };
  return (
    <span
      className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-full border px-2 py-[3px] text-xs font-semibold ${keepCase ? '' : 'uppercase tracking-[0.04em]'} ${tones[tone]} ${className}`}
    >
      {children}
    </span>
  );
}

type SelectableCardProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'title'> & {
  title: ReactNode;
  price?: ReactNode;
  details?: ReactNode;
  footer?: ReactNode;
  selected?: boolean;
  /** Sold out: rendered greyed, not focusable, with `soldOutLabel` in place of the price. */
  soldOut?: boolean;
  soldOutLabel?: ReactNode;
};

/** Format card with a 22 px checkbox (screen 01). The whole card is the label. */
export const SelectableCard = forwardRef<HTMLInputElement, SelectableCardProps>(
  function SelectableCard(
    { title, price, details, footer, selected, soldOut, soldOutLabel, ...rest },
    ref,
  ) {
    const body = (
      <span className="flex flex-1 flex-col gap-1">
        <span className="flex items-baseline justify-between gap-2">
          <span className={`text-lg font-bold ${soldOut ? 'text-ink-muted' : ''}`}>{title}</span>
          {soldOut ? (
            <Badge>{soldOutLabel}</Badge>
          ) : (
            price && (
              <span
                className={`font-display text-price font-bold ${selected ? 'text-accent' : ''}`}
              >
                {price}
              </span>
            )
          )}
        </span>
        {details && !soldOut && <span className="text-sm text-ink-muted">{details}</span>}
        {footer && !soldOut && <span className="mt-1">{footer}</span>}
      </span>
    );

    if (soldOut) {
      return (
        <div className="flex items-start gap-3.5 rounded-card border border-line bg-surface p-4 opacity-70">
          <span
            aria-hidden="true"
            className="mt-0.5 size-[22px] shrink-0 rounded-[5px] border-2 border-line-strong"
          />
          {body}
        </div>
      );
    }
    return (
      <label
        className={`flex cursor-pointer items-start gap-3.5 rounded-card p-4 ${
          selected ? 'border-2 border-accent bg-accent-soft' : 'border border-line bg-paper'
        }`}
      >
        <input
          ref={ref}
          type="checkbox"
          className="m-0 mt-0.5 size-[22px] shrink-0 accent-accent"
          {...rest}
        />
        {body}
      </label>
    );
  },
);

/** Horizontal rule with a centred label ("ou par carte", screen 03). */
export function Divider({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 text-helper text-ink-subtle">
      <span aria-hidden="true" className="h-px flex-1 bg-line" />
      {children}
      <span aria-hidden="true" className="h-px flex-1 bg-line" />
    </div>
  );
}

/** Key figure with its label (back-office dashboard tiles). */
export function Stat({
  label,
  value,
  hint,
  tone = 'ink',
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'ink' | 'danger';
}) {
  return (
    <Card className="gap-1!">
      <span className="text-helper font-semibold uppercase tracking-[0.04em] text-ink-muted">
        {label}
      </span>
      <span
        className={`font-display text-total font-bold tabular-nums ${tone === 'danger' ? 'text-danger' : 'text-ink'}`}
      >
        {value}
      </span>
      {hint && <span className="text-helper text-ink-muted">{hint}</span>}
    </Card>
  );
}
